import api from './api';
import { normalizePlantilla } from '../data/mockPlantillasCiclicos';

const basePath = '/produccion-concesionaria/plantillas-ciclicas';
const planeamientoDatosPath = '/produccion-concesionaria/planeamiento/datos-plantillas-ciclicos';

/** Evita peticiones duplicadas en montajes dobles (p. ej. React Strict Mode). */
let inflightPlaneamientoDatos = null;

function messageFromApiError(data) {
  if (!data) return 'Error de red o del servidor';
  if (data.errors && typeof data.errors === 'object') {
    return Object.values(data.errors)
      .flat()
      .filter(Boolean)
      .join(' ');
  }
  return data.message || 'Error al procesar la solicitud';
}

export async function fetchPlantillasCiclicas() {
  const res = await api.get(basePath);
  if (!res.data?.success || !Array.isArray(res.data.data)) {
    return [];
  }
  return res.data.data.map((row) => normalizePlantilla(row));
}

function parsePlaneamientoDatos(res) {
  const d = res.data?.data;
  if (!res.data?.success || !d) {
    throw new Error(messageFromApiError(res.data));
  }
  return {
    plantillas: (d.plantillas || []).map((row) => normalizePlantilla(row)),
    tiempos: d.tiempos || [],
    tiposPlato: d.tiposPlato || [],
  };
}

/**
 * Carga en una sola petición: plantillas, tiempos y tipos de plato (planeamiento).
 * Las llamadas concurrentes reutilizan la misma promesa HTTP.
 */
export function fetchPlaneamientoDatosPlantillasCiclicos() {
  if (!inflightPlaneamientoDatos) {
    inflightPlaneamientoDatos = api
      .get(planeamientoDatosPath)
      .then((res) => {
        inflightPlaneamientoDatos = null;
        return parsePlaneamientoDatos(res);
      })
      .catch((e) => {
        inflightPlaneamientoDatos = null;
        const data = e.response?.data;
        if (data) {
          throw new Error(messageFromApiError(data));
        }
        throw new Error(e.message || 'Error de conexión');
      });
  }
  return inflightPlaneamientoDatos;
}

/**
 * Importación masiva (una petición) para migrar varias plantillas, p. ej. desde localStorage.
 * @param {Array<{ nombre: string, proyectoId?: string|null, diasSemana: object, bloques: object[] }>} plantillas
 */
export async function importarPlantillasCiclicasBulk(plantillas) {
  try {
    const res = await api.post(`${basePath}/importar`, { plantillas });
    if (!res.data?.success || !Array.isArray(res.data.data?.plantillas)) {
      throw new Error(messageFromApiError(res.data));
    }
    return (res.data.data.plantillas || []).map((row) => normalizePlantilla(row));
  } catch (e) {
    const data = e.response?.data;
    if (data) throw new Error(messageFromApiError(data));
    throw new Error(e.message || 'Error de conexión');
  }
}

/**
 * @param {{ nombre: string, proyectoId?: string|null, diasSemana: object, bloques: object[] }} payload
 */
export async function createPlantillaCiclica(payload) {
  try {
    const res = await api.post(basePath, payload);
    if (!res.data?.success || !res.data.data) {
      throw new Error(messageFromApiError(res.data));
    }
    return normalizePlantilla(res.data.data);
  } catch (e) {
    const data = e.response?.data;
    if (data) throw new Error(messageFromApiError(data));
    throw new Error(e.message || 'Error de conexión');
  }
}

/**
 * @param {string|number} id
 * @param {{ nombre: string, proyectoId?: string|null, diasSemana: object, bloques: object[] }} payload
 */
export async function updatePlantillaCiclica(id, payload) {
  try {
    const res = await api.put(`${basePath}/${id}`, payload);
    if (!res.data?.success || !res.data.data) {
      throw new Error(messageFromApiError(res.data));
    }
    return normalizePlantilla(res.data.data);
  } catch (e) {
    const data = e.response?.data;
    if (data) throw new Error(messageFromApiError(data));
    throw new Error(e.message || 'Error de conexión');
  }
}

export async function deletePlantillaCiclica(id) {
  try {
    const res = await api.delete(`${basePath}/${id}`);
    if (!res.data?.success) {
      throw new Error(res.data?.message || 'Error al eliminar la plantilla');
    }
  } catch (e) {
    const data = e.response?.data;
    throw new Error(data?.message || messageFromApiError(data) || e.message);
  }
}
